import React, { useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Paper,
    type SelectChangeEvent,
    Stack,
    TextField,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Clear as ClearIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    FilterList as FilterIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import type { RegionDto } from '@/global/api/gen/ts/RegionDto';
import { RegionsAutocomplete } from '@/global/components/Autocompletes/RegionsAutocomplete';
import { CategoriesSelect } from '@/modules/categories/components/CategoriesSelect';

interface AutoSchoolSearchProps {
    searchQuery: string;
    onSearchChange: (query: string) => void;
    selectedCity?: number | null;
    onCityChange?: (city: RegionDto | null) => void;
    selectedCategories: string[];
    onCategoriesChange: (categories: string[]) => void;
    onClearFilters: () => void;
    disableCityFilter?: boolean;
}

export function AutoSchoolSearch({
                                     searchQuery,
                                     onSearchChange,
                                     selectedCity,
                                     onCityChange,
                                     selectedCategories,
                                     onCategoriesChange,
                                     onClearFilters
                                 }: AutoSchoolSearchProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showFilters, setShowFilters] = useState(false);

    const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        onCategoriesChange(typeof value === 'string' ? value.split(',') : value);
    };

    const hasActiveFilters = selectedCity || selectedCategories.length > 0 || searchQuery;

    return (
        <Paper
            elevation={2}
            sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'white',
                border: '1px solid #e9ecef'
            }}
        >
            <Box sx={{mb: isMobile ? 2 : 0}}>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={2}
                    alignItems={isMobile ? 'stretch' : 'center'}
                >
                    <TextField
                        fullWidth
                        placeholder="Поиск автошкол по названию..."
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{color: '#6c757d', mr: 1}}/>,
                            endAdornment: searchQuery && (
                                <IconButton
                                    size="small"
                                    onClick={() => onSearchChange('')}
                                    sx={{color: '#6c757d'}}
                                >
                                    <ClearIcon/>
                                </IconButton>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: '#f8f9fa',
                                '&:hover': {
                                    bgcolor: '#e9ecef'
                                }
                            }
                        }}
                    />

                    {isMobile && (
                        <Button
                            variant="outlined"
                            startIcon={<FilterIcon/>}
                            endIcon={showFilters ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                            onClick={() => setShowFilters(!showFilters)}
                            sx={{
                                minWidth: 'auto',
                                bgcolor: hasActiveFilters ? '#e3f2fd' : 'transparent',
                                color: hasActiveFilters ? '#1976d2' : '#6c757d'
                            }}
                        >
                            Фильтры
                            {' '}
                            {hasActiveFilters && `(${[selectedCity, ...selectedCategories].filter(Boolean).length})`}
                        </Button>
                    )}

                    {!isMobile && hasActiveFilters && (
                        <Button
                            variant="text"
                            startIcon={<ClearIcon/>}
                            onClick={onClearFilters}
                            sx={{color: '#6c757d', minWidth: 'auto'}}
                        >
                            Очистить
                        </Button>
                    )}
                </Stack>
            </Box>

            <Collapse in={!isMobile || showFilters}>
                <Box sx={{mt: 2}}>
                    <Stack spacing={2}>
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={2}
                            alignItems={isMobile ? 'stretch' : 'center'}
                        >
                            {
                                (onCityChange) && (
                                    <RegionsAutocomplete
                                        defaultId={selectedCity}
                                        label={'Выбор региона или города'}
                                        onChange={onCityChange}
                                        sx={{
                                            minWidth: isMobile ? '100%' : 200,
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: '#f8f9fa'
                                            }
                                        }}
                                        clearOnEscape
                                    />
                                )
                            }

                            <CategoriesSelect
                                formSx={{minWidth: isMobile ? '100%' : 200}}
                                value={selectedCategories}
                                onChange={handleCategoryChange}
                                multiple
                            />

                            {isMobile && hasActiveFilters && (
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon/>}
                                    onClick={onClearFilters}
                                    sx={{color: '#6c757d'}}
                                >
                                    Очистить фильтры
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Collapse>
        </Paper>
    );
}
